import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser, useAuth } from "../hooks/useAuthCompat";
import { toast } from "sonner";
import { useApi, publicApi } from "../hooks/useApi";
import { useAuthContext } from "../context/AuthContext";
import { CheckCircle2, ShieldCheck, Loader2 } from "lucide-react";

const PLAN_LABEL = { gold: "ENDURE BEYOND GOLD", gold_pro: "ENDURE BEYOND GOLD PRO" };

export default function GoldActivate() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const ticket = params.get("ticket");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const api = useApi();
  const { isSignedIn, isLoaded } = useUser();
  const { signOut } = useAuth();
  const { refresh } = useAuthContext();
  const attempted = useRef(false);
  const ticketAttempted = useRef(false);
  const [slowWait, setSlowWait] = useState(false);
  const [ticketFailed, setTicketFailed] = useState(false);
  const [consumingTicket, setConsumingTicket] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setSlowWait(true), 60000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (!ticket || isSignedIn || ticketAttempted.current) return;
    ticketAttempted.current = true;
    setConsumingTicket(true);
    (async () => {
      try {
        await publicApi.post("/auth/activate", { token: ticket });
        await refresh();
      } catch {
        setTicketFailed(true);
      } finally {
        setConsumingTicket(false);
      }
    })();
  }, [ticket, isSignedIn, refresh]);

  const { data: claim, isError: claimNotFound } = useQuery({
    queryKey: ["gold-claim", sessionId],
    queryFn: async () => (await api.get("/gold/enroll/claim", { params: { session_id: sessionId } })).data,
    enabled: !!sessionId,
    retry: false,
    refetchInterval: (query) => (query.state.status === "error" || ["claimed", "paid", "refunded"].includes(query.state.data?.status) ? false : 2500),
  });

  useEffect(() => {
    if (typeof claim?.resend_available_in_seconds === "number") setResendCooldown(claim.resend_available_in_seconds);
  }, [claim?.resend_available_in_seconds]);

  const redeem = useMutation({
    mutationFn: async () => api.post("/gold/enroll/claim/redeem", { session_id: sessionId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gold-membership"] });
      navigate("/vault", { replace: true });
    },
  });

  const resend = useMutation({
    mutationFn: async () => publicApi.post("/gold/enroll/claim/resend", { session_id: sessionId }),
    onSuccess: (res) => {
      toast.success("A new activation link is on its way to your email.");
      setResendCooldown(res.data?.resend_available_in_seconds || 60);
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail;
      const message = typeof detail === "string" ? detail : detail?.message || "Could not send a new link.";
      toast.error(message);
      if (detail?.retry_after_seconds) setResendCooldown(detail.retry_after_seconds);
      if (e?.response?.status === 410) queryClient.invalidateQueries({ queryKey: ["gold-claim", sessionId] });
    },
  });

  useEffect(() => {
    const ready = claim?.status === "paid" || claim?.status === "claimed";
    if (ready && isLoaded && isSignedIn && !attempted.current && !redeem.isPending) {
      attempted.current = true;
      redeem.mutate();
    }
  }, [claim, isLoaded, isSignedIn, redeem]);

  const planLabel = PLAN_LABEL[claim?.entitlement_product_type] || "Gold";

  if (!sessionId) {
    return (
      <div className="theme-gold min-h-screen flex items-center justify-center text-center px-6" data-testid="gold-activate-missing-session">
        <p className="text-textSecondary">Missing checkout session. <Link to="/gold" className="underline text-primary">Return to Gold</Link></p>
      </div>
    );
  }

  if (claimNotFound) {
    return (
      <div className="theme-gold min-h-screen flex items-center justify-center text-center px-6" data-testid="gold-activate-not-found">
        <div>
          <p className="text-textSecondary mb-4">We couldn't find a Gold checkout for this session.</p>
          <p className="text-textSecondary text-sm mb-6">If you just paid, check your email for an activation link, or <a href="mailto:support@endurebeyondpod.com" className="underline text-primary">contact support</a>.</p>
          <Link to="/gold" className="underline text-primary">Return to Gold</Link>
        </div>
      </div>
    );
  }

  if (claim?.status === "refunded") {
    return (
      <div className="theme-gold min-h-screen flex items-center justify-center text-center px-6" data-testid="gold-activate-refunded">
        <div>
          <h1 className="font-display text-xl mb-2">This Purchase Was Refunded</h1>
          <p className="text-textSecondary text-sm mb-6">
            This membership can no longer be activated. If you believe this is a mistake, <a href="mailto:support@endurebeyondpod.com" className="underline text-primary">contact support</a>.
          </p>
          <Link to="/gold" className="underline text-primary">Return to Gold</Link>
        </div>
      </div>
    );
  }

  const paid = claim?.status === "paid" || claim?.status === "claimed";
  const mismatchError = redeem.isError ? (redeem.error?.response?.data?.detail || "Could not activate your membership.") : null;

  return (
    <div className="theme-gold min-h-screen flex items-center justify-center px-6 py-16" data-testid="gold-activate-page">
      <div className="max-w-md w-full text-center">
        <img src="https://customer-assets.emergentagent.com/job_d18f6363-3058-4bfd-b277-f958917ab691/artifacts/i3gab7l7_EB%20Gold%20logo.png" alt="" className="w-8 h-8 mx-auto mb-6" />

        {!paid && (
          <div data-testid="gold-activate-confirming" className="flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 text-[#C9A227] animate-spin" />
            <p className="text-white/60 text-sm">Confirming your payment with Stripe…</p>
            {slowWait && (
              <p className="text-white/40 text-xs mt-4" data-testid="gold-activate-slow-wait">
                This is taking longer than expected. If you completed payment, check your email for an activation
                link, or <a href="mailto:support@endurebeyondpod.com" className="underline text-[#C9A227]">contact support</a>.
              </p>
            )}
          </div>
        )}

        {paid && !isSignedIn && consumingTicket && (
          <div data-testid="gold-activate-ticket-consuming" className="flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 text-[#C9A227] animate-spin" />
            <p className="text-white/60 text-sm">Activating your account…</p>
          </div>
        )}

        {paid && !isSignedIn && !consumingTicket && ticketFailed && claim?.status !== "claimed" && (
          <div data-testid="gold-activate-ticket-failed">
            <h1 className="font-display text-xl mb-2">This Link Has Expired</h1>
            <p className="text-white/60 text-sm mb-6">
              Your activation link for <span className="text-white font-medium">{claim.email}</span> is no longer valid.
              Request a fresh one, or create your password below.
            </p>
            <button
              data-testid="gold-activate-resend-button"
              onClick={() => resend.mutate()}
              disabled={resend.isPending || resendCooldown > 0}
              className="gold-metallic-btn px-5 py-2.5 text-sm uppercase tracking-wide rounded-lg disabled:opacity-50 mb-6"
            >
              {resend.isPending ? "Sending…" : resendCooldown > 0 ? `Try Again In ${resendCooldown}s` : "Send Me a New Link"}
            </button>
            <Link to={`/sign-up?email=${encodeURIComponent(claim.email)}`} data-testid="gold-activate-sign-up-link"
              className="block bg-black border border-white/10 rounded-xl py-3 text-sm text-white hover:border-[#C9A227] transition-colors">
              Set a password for {claim.email}
            </Link>
          </div>
        )}

        {paid && !isSignedIn && !consumingTicket && ticketFailed && claim?.status === "claimed" && (
          <div data-testid="gold-activate-already-claimed">
            <CheckCircle2 className="w-8 h-8 text-[#C9A227] mx-auto mb-3" />
            <h1 className="font-display text-xl mb-2">Already Activated</h1>
            <p className="text-white/60 text-sm mb-6">
              This membership is already active on an account. Sign in with <span className="text-white font-medium">{claim.email}</span> below to enter the Vault.
            </p>
            <Link to={`/sign-in?email=${encodeURIComponent(claim.email)}`} data-testid="gold-activate-sign-in-link"
              className="block bg-black border border-white/10 rounded-xl py-3 text-sm text-white hover:border-[#C9A227] transition-colors">
              Sign In as {claim.email}
            </Link>
          </div>
        )}

        {paid && !isSignedIn && !consumingTicket && !ticketFailed && !ticket && (
          <div data-testid="gold-activate-signin-prompt">
            <CheckCircle2 className="w-8 h-8 text-[#C9A227] mx-auto mb-3" />
            <h1 className="font-display text-xl mb-2">Payment Confirmed</h1>
            <p className="text-white/60 text-sm mb-6">
              Check your email for an activation link, or sign in below with <span className="text-white font-medium">{claim.email}</span>.
            </p>
            <Link to={`/sign-in?email=${encodeURIComponent(claim.email)}`} data-testid="gold-activate-sign-in-link"
              className="block bg-black border border-white/10 rounded-xl py-3 text-sm text-white hover:border-[#C9A227] transition-colors">
              Sign In as {claim.email}
            </Link>
          </div>
        )}

        {paid && isSignedIn && !mismatchError && (
          <div data-testid="gold-activate-redeeming" className="flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 text-[#C9A227] animate-spin" />
            <p className="text-white/60 text-sm">Activating your {planLabel} membership…</p>
          </div>
        )}

        {mismatchError && (
          <div data-testid="gold-activate-mismatch-error">
            <h1 className="font-display text-xl mb-3 text-[#C9A227]">
              {redeem.error?.response?.status === 403 ? "Email Does Not Match"
                : redeem.error?.response?.status === 409 ? "Already Activated On Another Account"
                : redeem.error?.response?.status === 410 ? "Purchase Refunded"
                : "Could Not Activate"}
            </h1>
            <p className="text-white/60 text-sm mb-6">{mismatchError}</p>
            {redeem.error?.response?.status === 403 && (
              <button
                data-testid="gold-activate-signout-button"
                onClick={() => signOut()}
                className="bg-white/5 text-white px-5 py-2.5 text-sm uppercase tracking-wide border border-white/10 hover:border-[#C9A227] rounded-lg transition-colors"
              >
                Sign Out &amp; Try Another Account
              </button>
            )}
          </div>
        )}

        <p className="text-xs text-white/40 flex items-center justify-center gap-1.5 mt-10">
          <ShieldCheck className="w-3.5 h-3.5" /> Verified via Stripe · Secured by Endure Beyond
        </p>
      </div>
    </div>
  );
}
